import { DropzoneFile } from 'components/Dropzone/utils';
import { DataRoom, DataRoomSecuritySettings } from './DataRoom';
import { DataRoomMember } from './DataRoomMember';
import { BulkAvailabilityState } from './DataRoomBulk';
import { User } from 'models/User';
import { ObjectValues } from 'types';
import { DataRoomShareableLinkConsumer } from 'models/ShareableLink';
import { CurrentLocation } from 'components/DataRoomNodeCurrentLocation/DataRoomNodeCurrentLocation.component';

export type DataRoomNode = {
  _id: string;
  id: string;

  name: string;
  sortName: string;

  document?: DropzoneFile; // document is file

  dataRoomId: string;
  dataRoom?: Partial<DataRoom> | null;
  dataRoomOwner?: DataRoomMember;

  isProcessedSuccessfully: boolean;
  failedConversionToPdf: boolean;
  hasFilesFailedConversionToPdf: boolean; // for folders

  hasAuthorizedActions: boolean;
  canDownload: boolean;

  numberOfMembersWithAccess: number;

  createdAt: Date;
  updatedAt: Date;
  modifiedAt: Date;

  tag: DataRoomNodeTag | null;

  // only for file
  denyTagNudging?: boolean;

  childrenIds?: string[]; // id of files in folder
  directChildrenIds: string[]; // id of direct children files in folder

  buySideFileIds?: string[]; // id of buyer files in folder
  isBuyerFile?: boolean; // is buyer file

  enabledState?: BulkAvailabilityState;
  folderSize?: number;
  numberOfViews: number;
  numberOfDownloads: number;

  //buy side view
  buyerNumberOfViews?: number;
  buyerNumberOfDownloads?: number;

  //version
  version: number;

  // folder
  numberOfAccessableFilesForDownload: number;

  docIndex?: string;
  hasInvalidDocIndex?: boolean;

  userId: string;

  leadNumberOfViews?: number;
  leadNumberOfDownloads?: number;
  leadLastAccess?: Date;

  viewedDate?: Date;

  allowSoftSignals?: boolean;
} & DataRoomSecuritySettings &
  CurrentLocation;

export const DATA_ROOM_NODE_TAG = {
  AL: 'AL',
  BS: 'BS',
  CA: 'CA',
  CS: 'CS',
  EO: 'EO',
  ES: 'ES',
  FM: 'FM',
  HA: 'HA',
  IM: 'IM',
  NB: 'NB',
  PA: 'PA',
  PD: 'PD',
  PL: 'PL',
  SP: 'SP',
  T: 'T',
  TS: 'TS',
  CL: 'CL',
  SL: 'SL',
  BAS: 'BAS',
  SR: 'SR',
  NDA: 'NDA',
} as const;

export type DataRoomNodeTag = ObjectValues<typeof DATA_ROOM_NODE_TAG>;

export const DATA_ROOM_NODE_LONG_TAGS: Record<DataRoomNodeTag, string> = {
  AL: 'ALlabel',
  BS: 'BSlabel',
  CA: 'CAlabel',
  CS: 'CSlabel',
  ES: 'ESlabel',
  FM: 'FMlabel',
  HA: 'HAlabel',
  IM: 'IMlabel',
  NB: 'NBlabel',
  PL: 'PLlabel',
  SP: 'SPlabel',
  T: 'Tlabel',
  TS: 'TSlabel',
  CL: 'CLlabel',
  SL: 'SLlabel',
  BAS: 'BASlabel',
  SR: 'SRlabel',
  PD: 'PDlabel',
  EO: 'EOlabel',
  PA: 'PAlabel',
  NDA: 'NDAlabel',
} as const;

export const DATA_ROOM_NODE_FULL_TAGS: Record<DataRoomNodeTag, string> = {
  AL: 'fullALlabel',
  BS: 'fullBSlabel',
  CA: 'fullCAlabel',
  CS: 'fullCSlabel',
  ES: 'fullESlabel',
  FM: 'fullFMlabel',
  HA: 'fullHAlabel',
  IM: 'fullIMlabel',
  NB: 'fullNBlabel',
  PL: 'fullPLlabel',
  SP: 'fullSPlabel',
  T: 'fullTlabel',
  TS: 'fullTSlabel',
  CL: 'fullCLlabel',
  SL: 'fullSLlabel',
  BAS: 'fullBASlabel',
  SR: 'fullSRlabel',
  PD: 'fullPDlabel',
  EO: 'fullEOlabel',
  PA: 'fullPAlabel',
  NDA: 'fullNDAlabel',
} as const;

export const DATA_ROOM_NODE_SHORT_TAGS: Record<DataRoomNodeTag, string> = {
  AL: 'AL',
  BS: 'BS',
  CA: 'CA',
  CS: 'CS',
  ES: 'ES',
  FM: 'FM',
  HA: 'HA',
  IM: 'IM',
  NB: 'NB',
  PL: 'PL',
  SP: 'SP',
  T: 'T',
  TS: 'TS',
  CL: 'CL',
  SL: 'SL',
  BAS: 'BAS',
  SR: 'SR',
  PD: 'PD',
  EO: 'EO',
  PA: 'PA',
  NDA: 'NDA',
} as const;

export const DATA_ROOM_NODE_1_T_1_TAGS = [
  DATA_ROOM_NODE_TAG.CA,
  DATA_ROOM_NODE_TAG.CS,
  DATA_ROOM_NODE_TAG.ES,
  DATA_ROOM_NODE_TAG.IM,
  DATA_ROOM_NODE_TAG.NB,
  DATA_ROOM_NODE_TAG.CL,
  DATA_ROOM_NODE_TAG.SL,
  DATA_ROOM_NODE_TAG.BAS,
  DATA_ROOM_NODE_TAG.SR,
  DATA_ROOM_NODE_TAG.BS,
  DATA_ROOM_NODE_TAG.PL,
];

export const DATA_ROOM_NODE_VERSION_TYPE = {
  FILE_RENAME: 'file-rename',
  FILE_UPLOAD: 'file-upload',
} as const;

type DataRoomNodeVersionType =
  typeof DATA_ROOM_NODE_VERSION_TYPE[keyof typeof DATA_ROOM_NODE_VERSION_TYPE];

export type DataRoomNodeVersion = {
  _id: string;
  type: DataRoomNodeVersionType;
  version: number;
  name: string;
  sortName: string;
  previousName: string;

  // Can be null if type of version is "file-rename"
  documentId: string | null;
  document: DropzoneFile | null;

  createdAt: Date;
  updatedAt: Date;
  dataRoomId: string;
  dataRoom: Partial<DataRoom> | null;
  dataRoomNodeId: string;
  dataRoomNode: Partial<DataRoomNode> | null;
  dataRoomMemberId: string;
  dataRoomMember: Partial<DataRoomMember> | null;
  userId: string;
  user: Partial<User> | null;
};

export type DataRoomAnalytics = {
  index: number;
  thumbnail: DropzoneFile;
  numberOfSecondsSpent: number;
};

export const DATA_ROOM_NODE_TYPE = {
  FILE: 'file',
  FOLDER: 'folder',
};

export type DataRoomNodeType = ObjectValues<typeof DATA_ROOM_NODE_TYPE>;

export type DataRoomMembersWithAccess = {
  buySideMembers: DataRoomMember[];
  sellSideMembers: DataRoomMember[];
  shareableLinkConsumers: DataRoomShareableLinkConsumer[];
};
